import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Box,
  Button,
  Divider,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useQuestionRecord } from "../../../context/QuestionRecord";
import ROUTES_NAME from "../../../constants/RoutesName";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import { submitStatus, submitApi } from "../../../services/service";
import socket from "../../../socket/socket";

function SubmitSummary({
  sectionNames,
  question,
  setIsOnSubmit,
  sscTimeUp,
  handleRemove,
}) {
  const { questionRecords } = useQuestionRecord();
  const [submitSummaryD, setSubmitSummaryD] = useState([]);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdownTime, setCountdownTime] = useState(10);
  const navigate = useNavigate();

  // Memoized initial summary creation
  const initialSummary = useMemo(() => {
    if (question?.subjects) {
      return question.subjects.flatMap((subject) => ({
        subject_id: subject.id || 0,
        subject_name: subject.subject_name || "Unnamed Subject",
        sections: (subject.sections || []).map((section) => ({
          section_id: section.id || 0,
          section_name: section.section_name || "Unnamed Section",
          totalQuestion: Number(section.question_count) || 0,
          answered: 0,
          notAns: 0,
          markedWithAns: 0,
          markedWithoutAns: 0,
          notVisited: Number(section.question_count) || 0,
        })),
      }));
    } else if (sectionNames?.length) {
      return [
        {
          subject_id: 0,
          subject_name: "Combined Sections",
          sections: sectionNames.flat().map((section) => ({
            section_id: section.section_id,
            section_name: section.name || "Unnamed Section",
            totalQuestion: 0,
            answered: 0,
            notAns: 0,
            markedWithAns: 0,
            markedWithoutAns: 0,
            notVisited: 0,
          })),
        },
      ];
    }
    return [];
  }, [question, sectionNames]);

  // Initialize or update summary data
  useEffect(() => {
    setSubmitSummaryD(initialSummary);
  }, [initialSummary]);

  // Memoized section question counts
  const updatedSummary = useMemo(() => {
    if (!submitSummaryD.length || !questionRecords?.length)
      return submitSummaryD;

    // Create a map for quick section_id lookup
    const sectionMap = new Map(
      questionRecords.map((q) => [Number(q.section_id), q])
    );

    return submitSummaryD.map((subject) => ({
      ...subject,
      sections: subject.sections.map((section) => {
        const sectionQuestions = questionRecords.filter(
          (q) => Number(q.section_id) === Number(section.section_id)
        );

        const answered = sectionQuestions.reduce(
          (count, q) => (q.attempt_status === "attempted" ? count + 1 : count),
          0
        );

        const notAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "not answered" ? count + 1 : count,
          0
        );

        const markedWithAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "marked" &&
            (q.option_id || q.multiple_option_ids?.length || q.answer)
              ? count + 1
              : count,
          0
        );

        const markedWithoutAns = sectionQuestions.reduce(
          (count, q) =>
            q.attempt_status === "marked" &&
            !q.option_id &&
            !q.multiple_option_ids?.length &&
            !q.answer
              ? count + 1
              : count,
          0
        );

        const notVisited = Math.max(
          0,
          section.totalQuestion -
            (answered + markedWithAns + markedWithoutAns + notAns)
        );

        return {
          ...section,
          answered,
          notAns,
          markedWithAns,
          markedWithoutAns,
          notVisited,
        };
      }),
    }));
  }, [questionRecords, submitSummaryD]);

  // Update summary only when counts actually change
  useEffect(() => {
    const shouldUpdate =
      JSON.stringify(submitSummaryD) !== JSON.stringify(updatedSummary);
    if (shouldUpdate) {
      setSubmitSummaryD(updatedSummary);
    }
  }, [updatedSummary]);

  // const handleRemove = () => {
  //   setIsOnSubmit(false);
  //   sessionStorage.removeItem("isOnsubmit");
  //   window.location.reload();
  // };

  useEffect(() => {
    if (sscTimeUp) {
      const timer = setInterval(() => {
        setCountdownTime((prevTime) => {
          const newTime = prevTime - 1;
          return newTime;
        });
      }, 1000);

      const timeout = setTimeout(() => {
        clearInterval(timer);
        if (sessionStorage.getItem("submitByH")) {
          navigate(ROUTES_NAME.THANK_YOU);
        } else {
          submit("time");
          navigate(ROUTES_NAME.THANK_YOU);
        }
      }, 10000);

      return () => {
        clearTimeout(timeout);
        clearInterval(timer);
      };
    }
  }, [sscTimeUp]);

  const submit = async (type) => {
    const examId = sessionStorage.getItem("examId");
    setLoading(true);

    try {
      const res = await submitApi({ examId, type });
      if (res.success) {
        setLoading(false);
        const examId = sessionStorage.getItem("examId");
        const userId = sessionStorage.getItem("userId");
        socket.emit("changeStatus", {
          examId,
          userId,
          status: "Submitted",
        });
        const branch_id = sessionStorage.getItem("selectedBranch");
        const center_id = sessionStorage.getItem("selectedCenter");
        const userAuth = JSON.parse(sessionStorage.getItem("userAuth"));
        const exam_name = sessionStorage.getItem("exam_name");
        sessionStorage.clear();
        sessionStorage.setItem("exam_name", exam_name);
        sessionStorage.setItem("userId", userId);
        sessionStorage.setItem("selectedBranch", branch_id);
        sessionStorage.setItem("selectedCenter", center_id);
        sessionStorage.setItem("userAuth", JSON.stringify(userAuth));
        sessionStorage.setItem("candidateLoggedinToDashboard", true);

        if (userId === "4660") {
          navigate(ROUTES_NAME.EXAM_SCORE, {
            state: { examResult: JSON.parse(res.examResult).result },
          });
        } else {
          navigate(ROUTES_NAME.THANK_YOU);
        }
      }
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    socket.connect();
    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <>
      {submitSummaryD.length === 0 && <Loader />}

      {loading && (
        <Loader
          message={
            "Your exam will be submitted in a few seconds, please wait..."
          }
        />
      )}
      <div>
        {agree ? (
          <>
            <div
              style={{
                // position: "fixed",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "90vh",
              }}
            >
              <div>
                <Typography
                  variant="body2"
                  component="div"
                  style={{ margin: "auto", textAlign: "center" }}
                >
                  Are you sure to submit exam?
                </Typography>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    margin: "15px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      submit("user");
                    }}
                    sx={{ minWidth: 100, mx: 2 }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    // color="secondary"
                    onClick={handleRemove}
                    sx={{ minWidth: 100, mx: 2 }}
                  >
                    No
                  </Button>{" "}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                fontWeight: "bold",
                textAlign: "center",
                borderBottom: "1px solid black",
                paddingBottom: "10px",
              }}
            >
              Exam Summary
            </Typography>
            {sscTimeUp && countdownTime && (
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  fontWeight: "600",
                  textAlign: "center",
                  borderBottom: "1px solid black",
                  paddingBottom: "10px",
                }}
              >
                {sessionStorage.getItem("submitByH")
                  ? "Your exam has been submitted by the head authority!"
                  : "Time Up"}{" "}
                : {countdownTime}s
              </Typography>
            )}

            <div
              style={{
                overflowY: "scroll",
                background: "",
                height: `${
                  countdownTime === 10
                    ? "calc(100vh - 180px)"
                    : "calc(100vh - 180px)"
                }`,
              }}
            >
              {submitSummaryD.map((subject) => (
                <Box p={2}>
                  <Typography style={{ paddingTop: 10, paddingBottom: 10 }}>
                    Subject : {subject.subject_name}
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small" aria-label="exam summary table">
                      <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableRow>
                          <TableCell>Section Name</TableCell>
                          <TableCell align="center">No. of Questions</TableCell>
                          <TableCell align="center">Answered</TableCell>
                          <TableCell align="center">Not Answered</TableCell>
                          <TableCell align="center">
                            Marked for Review
                          </TableCell>
                          <TableCell align="center">
                            Answered & Marked for Review
                          </TableCell>
                          <TableCell align="center">Not Visited</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subject.sections?.map((section, index) => (
                          <TableRow key={index}>
                            <TableCell>{section.section_name}</TableCell>
                            <TableCell align="center">
                              {section.totalQuestion}
                            </TableCell>
                            <TableCell align="center">
                              {section.answered}
                            </TableCell>
                            <TableCell align="center">
                              {section.notAns}
                            </TableCell>
                            <TableCell align="center">
                              {section.markedWithoutAns}
                            </TableCell>
                            <TableCell align="center">
                              {section.markedWithAns}
                            </TableCell>
                            <TableCell align="center">
                              {section.notVisited}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </div>

            {countdownTime === 10 ? (
              <div
                style={{
                  position: "fixed",
                  width: "100%",
                  bottom: 0,
                  borderTop: "1px solid black",
                  paddingTop: "10px",
                }}
              >
                <Typography
                  variant="body2"
                  component="div"
                  style={{
                    margin: "auto",
                    width: "50%",
                    textAlign: "center",
                  }}
                >
                  Are you sure to submit this Subject? Click 'Yes' to proceed;
                  Click 'No' to go back. Dear Candidate, Once the Subject is
                  submitted, you cannot edit your responses.
                </Typography>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    margin: "15px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setAgree(true)}
                    sx={{ minWidth: 100, mx: 2 }}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    // color="secondary"
                    onClick={handleRemove}
                    sx={{ minWidth: 100, mx: 2 }}
                  >
                    No
                  </Button>{" "}
                </div>
              </div>
            ) : (
              <div
                style={{
                  position: "fixed",
                  width: "100%",
                  bottom: 0,
                  borderTop: "1px solid black",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    submit("time");
                  }}
                  sx={{ minWidth: 100, mx: 2 }}
                >
                  submit
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SubmitSummary;
